import React, { useState } from 'react';
import { useSubmissions } from '../SubmissionsContext';
import SubmissionModal from '../components/SubmissionModal';

const DECLINE_REASONS = [
  'Photo quality too low',
  'Caption needs more detail',
  'Content not suitable for sharing',
  'Duplicate moment already submitted',
  'Missing GDPR confirmation',
];

const s = {
  page: { padding: '20px 16px 24px', maxWidth: '680px', margin: '0 auto' },
  heading: { fontSize: '20px', fontWeight: '700', color: '#222', marginBottom: '4px' },
  sub: { fontSize: '13px', color: '#888', marginBottom: '24px' },
  badge: {
    display: 'inline-block', background: '#fce8ef', color: '#DB3267',
    borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700', marginLeft: '8px',
  },
  card: {
    background: '#fff', borderRadius: '12px', padding: '18px 20px',
    marginBottom: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    borderLeft: '4px solid #DB3267',
  },
  cardApproved: { borderLeft: '4px solid #2e7d32' },
  cardDeclined: { borderLeft: '4px solid #e0e0e0', opacity: 0.6 },
  cardDeleted: { borderLeft: '4px solid #9e9e9e', opacity: 0.7 },
  deletedTag: {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    background: '#f5f5f5', color: '#757575', borderRadius: '6px',
    padding: '4px 10px', fontSize: '12px', fontWeight: '600', marginBottom: '10px',
  },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' },
  about: { fontSize: '16px', fontWeight: '700', color: '#222' },
  catBadge: {
    background: '#f3e5f5', color: '#6a1b9a', borderRadius: '20px',
    padding: '2px 10px', fontSize: '11px', fontWeight: '600',
  },
  meta: { fontSize: '12px', color: '#888', marginBottom: '10px' },
  caption: { fontSize: '14px', color: '#444', lineHeight: '1.6', marginBottom: '14px' },
  photoBadge: { fontSize: '12px', color: '#888', marginBottom: '14px' },
  photoStrip: { display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' },
  photoThumb: { width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e0e0e0' },
  noPhoto: { fontSize: '12px', color: '#bbb', marginBottom: '14px', fontStyle: 'italic' },
  actions: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  approveBtn: {
    padding: '9px 18px', background: '#e8f5e9', color: '#2e7d32',
    borderRadius: '8px', fontWeight: '700', fontSize: '13px', border: '1.5px solid #a5d6a7',
  },
  declineBtn: {
    padding: '9px 18px', background: '#fff', color: '#888',
    borderRadius: '8px', fontWeight: '600', fontSize: '13px', border: '1.5px solid #e0e0e0',
  },
  declinePanel: {
    marginTop: '14px', padding: '14px', background: '#f7f7f9',
    borderRadius: '8px', border: '1px solid #e0e0e0',
  },
  declinePanelTitle: { fontSize: '12px', fontWeight: '700', color: '#555', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  reasonBtn: {
    display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px',
    background: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px',
    marginBottom: '6px', fontSize: '13px', color: '#444', cursor: 'pointer',
  },
  cancelBtn: { padding: '8px 14px', background: 'none', color: '#888', fontSize: '13px', border: 'none', cursor: 'pointer' },
  statusApproved: { display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#2e7d32', fontWeight: '700', fontSize: '13px' },
  statusDeclined: { display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#888', fontSize: '13px' },
  reasonTag: { fontSize: '12px', color: '#888', fontStyle: 'italic' },
  emptyState: { textAlign: 'center', padding: '48px 20px', color: '#aaa' },
  emptyIcon: { fontSize: '48px', marginBottom: '12px' },
};

export default function AdminQueue() {
  const { submissions, updateStatus, pinSubmission, unpinSubmission } = useSubmissions();
  const [decliningId, setDecliningId] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [removedPhotos, setRemovedPhotos] = useState({});

  function togglePhotoRemoval(subId, idx) {
    setRemovedPhotos(prev => {
      const current = new Set(prev[subId] || []);
      if (current.has(idx)) current.delete(idx); else current.add(idx);
      return { ...prev, [subId]: new Set(current) };
    });
  }

  async function approve(id, photos) {
    const removed = removedPhotos[id];
    const filteredPhotos = (removed && removed.size > 0 && Array.isArray(photos))
      ? photos.filter((_, i) => !removed.has(i))
      : undefined;
    await updateStatus(id, 'approved', undefined, filteredPhotos);
    setRemovedPhotos(prev => { const n = { ...prev }; delete n[id]; return n; });
  }

  async function decline(id, reason) {
    await updateStatus(id, 'declined', reason);
    setDecliningId(null);
  }

  const deleted = submissions.filter(s => s.deletedByUser);
  const filtered = filter === 'deleted'
    ? deleted
    : submissions.filter(s => !s.deletedByUser && (filter === 'all' || s.status === filter));
  const pendingCount = submissions.filter(s => s.status === 'pending' && !s.deletedByUser).length;

  return (
    <div style={s.page}>
      {selected && (
        <SubmissionModal
          sub={selected}
          onClose={() => setSelected(null)}
          onApprove={(id) => { approve(id, selected?.photos); setSelected(null); }}
          onDecline={(id, reason) => { decline(id, reason); setSelected(null); }}
        />
      )}
      <h1 style={s.heading}>
        Review Queue
        {pendingCount > 0 && <span style={s.badge}>{pendingCount} pending</span>}
      </h1>
      <p style={s.sub}>Review staff submissions before they are shared with the team</p>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {['pending', 'approved', 'declined', 'all', 'deleted'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '7px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '600',
              background: filter === f ? (f === 'deleted' ? '#9e9e9e' : '#DB3267') : '#fff',
              color: filter === f ? '#fff' : '#888',
              border: filter === f ? 'none' : '1px solid #e0e0e0',
            }}
          >
            {f === 'deleted' ? `🗑️ Deleted${deleted.length > 0 ? ` (${deleted.length})` : ''}` : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={s.emptyState}>
          <div style={s.emptyIcon}>✅</div>
          <div>No {filter} submissions</div>
        </div>
      )}

      {filtered.map(sub => (
        <div
          key={sub.id}
          style={{
            ...s.card,
            ...(sub.deletedByUser ? s.cardDeleted : {}),
            ...(sub.status === 'approved' && !sub.deletedByUser ? s.cardApproved : {}),
            ...(sub.status === 'declined' && !sub.deletedByUser ? s.cardDeclined : {}),
            cursor: 'pointer',
          }}
          onClick={() => setSelected(sub)}
        >
          {sub.deletedByUser && (
            <div style={s.deletedTag}>
              🗑️ Removed by {sub.deletedBy || sub.submittedBy}
              {sub.deletedAt && ` · ${sub.deletedAt.slice(0, 10)}`}
            </div>
          )}

          <div style={s.topRow}>
            <div style={s.about}>{sub.about}</div>
            <span style={s.catBadge}>{sub.category}</span>
          </div>

          <div style={s.meta}>
            {sub.submittedBy} &nbsp;·&nbsp; {sub.shift} shift &nbsp;·&nbsp; {sub.submittedAt}
          </div>

          <div style={s.caption}>{sub.caption}</div>

          {Array.isArray(sub.photos) && sub.photos.length > 0 ? (
            <>
              {sub.status === 'pending' && !sub.deletedByUser && (
                <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '6px' }}>
                  Tap ✕ to remove a photo before approving
                  {removedPhotos[sub.id]?.size > 0 && (
                    <span style={{ color: '#e53935', fontWeight: '700', marginLeft: '6px' }}>
                      {removedPhotos[sub.id].size} removed
                    </span>
                  )}
                </div>
              )}
              <div style={s.photoStrip}>
                {sub.photos.map((p, i) => {
                  const isRemoved = sub.status === 'pending' && removedPhotos[sub.id]?.has(i);
                  return (
                    <div key={i} style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={p.url}
                        alt={`submission ${i + 1}`}
                        style={{
                          ...s.photoThumb,
                          opacity: isRemoved ? 0.25 : 1,
                          filter: isRemoved ? 'grayscale(1)' : 'none',
                        }}
                      />
                      {sub.status === 'pending' && !sub.deletedByUser && (
                        <button
                          onClick={e => { e.stopPropagation(); togglePhotoRemoval(sub.id, i); }}
                          style={{
                            position: 'absolute', top: '3px', right: '3px',
                            width: '20px', height: '20px',
                            background: isRemoved ? '#e8f5e9' : '#e53935',
                            color: isRemoved ? '#2e7d32' : '#fff',
                            border: 'none', borderRadius: '50%',
                            fontSize: '12px', fontWeight: '700', lineHeight: 1,
                            cursor: 'pointer', padding: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                          }}
                          title={isRemoved ? 'Restore photo' : 'Remove this photo'}
                        >
                          {isRemoved ? '+' : '✕'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : typeof sub.photos === 'number' ? (
            <div style={s.noPhoto}>📷 {sub.photos} photo{sub.photos !== 1 ? 's' : ''} (demo — no preview)</div>
          ) : null}

          {sub.status === 'pending' && !sub.deletedByUser && (
            <>
              <div style={s.actions}>
                <button style={s.approveBtn} onClick={() => approve(sub.id, sub.photos)}>
                  ✓ Approve &amp; Share{removedPhotos[sub.id]?.size > 0 ? ` (${(Array.isArray(sub.photos) ? sub.photos.length : 0) - removedPhotos[sub.id].size} photo${((Array.isArray(sub.photos) ? sub.photos.length : 0) - removedPhotos[sub.id].size) !== 1 ? 's' : ''})` : ''}
                </button>
                <button style={s.declineBtn} onClick={() => setDecliningId(decliningId === sub.id ? null : sub.id)}>
                  Decline
                </button>
              </div>

              {decliningId === sub.id && (
                <div style={s.declinePanel}>
                  <div style={s.declinePanelTitle}>Select a reason</div>
                  {DECLINE_REASONS.map(r => (
                    <button key={r} style={s.reasonBtn} onClick={() => decline(sub.id, r)}>{r}</button>
                  ))}
                  <button style={s.cancelBtn} onClick={() => setDecliningId(null)}>Cancel</button>
                </div>
              )}
            </>
          )}

          {sub.status === 'approved' && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={s.statusApproved}>✓ Approved &amp; shared</div>
              <button
                onClick={e => { e.stopPropagation(); sub.isPinned ? unpinSubmission(sub.id) : pinSubmission(sub.id); }}
                style={{
                  padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700',
                  background: sub.isPinned ? '#fff8e1' : '#f5f5f5',
                  color: sub.isPinned ? '#f57f17' : '#aaa',
                  border: sub.isPinned ? '1px solid #ffe082' : '1px solid #e0e0e0',
                }}
                title={sub.isPinned ? 'Unpin — remove from permanent storage' : 'Pin — keep this post safe across deployments'}
              >
                {sub.isPinned ? '📌 Pinned' : '📌 Pin'}
              </button>
            </div>
          )}

          {sub.status === 'declined' && (
            <div>
              <div style={s.statusDeclined}>✕ Declined</div>
              {sub.declineReason && <div style={s.reasonTag}>Reason: {sub.declineReason}</div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
